import { COMPANY_COUNTRIES, COMPANY_TYPES, COMPANY_TYPES_DIVISION, COMPANY_TYPES_SALES_COMPANY } from '@common/constants/company.constants'
import { CompanyType } from '@common/enums/company.enums'
import { SelectOptionType } from '@common/enums/global.enums'
import { Company, CompanyFormValues } from '@common/types/company.types'
import { Option } from '@common/types/global.types'
import TextInput from '@components/arthurBased/inputs/TextInput'
import SelectInput from '@components/inputs/SelectInput'
import PopIn from '@components/layout/PopIn'
import { Stack, styled } from '@mui/material'
import { useCompanyApi } from '@services/api/company.api'
import { useAuth } from '@services/contexts/AuthContext'
import { useSnackbar } from '@services/contexts/SnackbarContext'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

type CompanyCreationPopInProps = {
	isOpen: boolean
	onClose: () => void
	onSuccess: (company: Company) => void
}

/**
 * Company creation pop-in
 * @param isOpen
 * @param onClose
 * @param onSuccess
 * @constructor
 */
const CompanyCreationPopIn = (
	{
		isOpen,
		onClose,
		onSuccess
	}: CompanyCreationPopInProps
) => {
	const { user } = useAuth()
	const { createCompany } = useCompanyApi()
	const { formatMessage } = useIntl()
	const { addErrorSnackbar, addSnackbar } = useSnackbar()
	const [companyTypes, setCompanyTypes] = useState(COMPANY_TYPES)

	const {
		control,
		handleSubmit,
		reset
	} = useForm<CompanyFormValues>({
		defaultValues: {
			type: '',
			country: '',
			name: ''
		}
	})

	const { mutate: createCompanyMutate } = useMutation<Company, Error, CompanyFormValues>({
		mutationKey: ['createCompany'],
		mutationFn: (formValues) => createCompany(formValues),
		onSuccess: (data) => {
			onSuccess(data)
			addSnackbar(formatMessage({ id: 'dashboard.trainees.actions.createCompany.success' }))
		},
		onError: () => addErrorSnackbar()
	})

	const onSubmit = (values: CompanyFormValues) => createCompanyMutate(values)

	const options = useMemo((): Option[] => {
		const countries: Option[] = COMPANY_COUNTRIES.map(country => ({
			type: SelectOptionType.OPTION,
			label: formatMessage({ id: `global.enums.companyCountry.${country}` }),
			value: country as string
		}))

		// Sort translated labels
		countries.sort((a, b) => a.label.localeCompare(b.label))

		return countries
	}, [])

	useEffect(() => {
		if (!!user.company) {
			const { type } = user.company

			if (type === CompanyType.SALES_COMPANY) {
				setCompanyTypes(COMPANY_TYPES_SALES_COMPANY)
			} else if (type === CompanyType.DIVISION) {
				setCompanyTypes(COMPANY_TYPES_DIVISION)
			}

			reset({
				type: type === CompanyType.DISTRIBUTOR ? CompanyType.END_USER : '',
				country: '',
				name: ''
			})
		}
	}, [user])

	return (
		<PopIn
			buttons={[{
				label: formatMessage({ id: 'dashboard.trainees.actions.createCompany.buttons.confirm.label' }),
				onClick: handleSubmit(onSubmit)
			}]}
			isOpen={isOpen}
			onClose={onClose}
			subtitle={formatMessage({ id: 'dashboard.trainees.actions.createCompany.subtitle' })}
			title={formatMessage({ id: 'dashboard.trainees.actions.createCompany.title' })}
		>
			<Container>
				{
					(!!user.company && user.company.type !== CompanyType.DISTRIBUTOR) && (
						<SelectInput
							control={control}
							name="type"
							label={formatMessage({ id: 'global.fields.type' })}
							placeholder={formatMessage({ id: 'dashboard.trainees.actions.createCompany.fields.type.placeholder' })}
							options={companyTypes.map(option => ({
								type: SelectOptionType.OPTION,
								label: formatMessage({ id: `global.enums.companyType.${option}` }),
								value: option
							}))}
							isRequired
						/>
					)
				}
				<SelectInput
					control={control}
					name="country"
					label={formatMessage({ id: 'global.fields.country' })}
					placeholder={formatMessage({ id: 'dashboard.trainees.actions.createCompany.fields.country.placeholder' })}
					options={options}
					isRequired
				/>
				<TextInput
					control={control}
					name="name"
					label={formatMessage({ id: 'global.fields.name' })}
					placeholder={formatMessage({ id: 'dashboard.trainees.actions.createCompany.fields.name.placeholder' })}
					isRequired
				/>
			</Container>
		</PopIn>
	)
}

const Container = styled(Stack)({
	rowGap: '16px'
})

export default CompanyCreationPopIn
